/**
 * API client for Centre Operations -> Newsletter drafts.
 *
 * Backend: oh_operations Django app, alfred-suffixed endpoints under
 * /api/v1/oh_operations/newsletter/drafts/. Drafts store the whole form state
 * (photos as data URLs) in `data`; the list endpoint omits `data` on purpose.
 */
import { sentryLogger } from 'sentry'

import { apiEndpoints } from './common'

import { makeRequestUrl } from '../app/helpers'
import { get, patch, post, remove } from '../request'

// Draft picker rows for a centre — uuid/name/modified only (no data payload).
const fetchNewsletterDrafts = async (centreUuid) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.newsletterDrafts}/alfred`, {
        centre: centreUuid
      })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching newsletter drafts')
    throw error
  }
}

// Full draft including `data`, to hydrate the form.
const fetchNewsletterDraft = async (uuid) => {
  try {
    const response = await get(
      `${apiEndpoints.newsletterDrafts}/${uuid}/alfred`
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching newsletter draft')
    throw error
  }
}

// Save a draft. Upserts by (centre, name) server-side, so re-saving
// "August 2025" replaces that draft instead of stacking copies.
const saveNewsletterDraft = async ({
  centre,
  name,
  data,
  commType,
  category
}) => {
  try {
    const response = await post(`${apiEndpoints.newsletterDrafts}/alfred`, {
      centre,
      name,
      data,
      comm_type: commType,
      category
    })
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in saving newsletter draft')
    throw error
  }
}

// Freeze a draft for good — the backend then refuses every edit/delete.
// One-way by design; there is no unfreeze.
const freezeNewsletterDraft = async (uuid) => {
  try {
    const response = await post(
      `${apiEndpoints.newsletterDrafts}/${uuid}/freeze/alfred`,
      {}
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in freezing newsletter draft')
    throw error
  }
}

// Rename and/or overwrite an existing draft.
const updateNewsletterDraft = async (uuid, { name, data, category }) => {
  try {
    const response = await patch(
      `${apiEndpoints.newsletterDrafts}/${uuid}/alfred`,
      { name, data, category }
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in updating newsletter draft')
    throw error
  }
}

const deleteNewsletterDraft = async (uuid) => {
  try {
    await remove(`${apiEndpoints.newsletterDrafts}/${uuid}/alfred`)
    return uuid
  } catch (error) {
    sentryLogger(error, 'error in deleting newsletter draft')
    throw error
  }
}

// Generate AI artwork for an Event Creative. Returns {image: dataUrl}.
// 503 until the server has an image-API key configured.
//
// promptOnly skips the model and returns just {prompt}: image generation has no
// free tier on any provider, so until billing is live the builder copies this
// prompt into the Gemini app by hand. Same server-side prompt either way — the
// wording must not fork.
const generateEventCreativeImage = async ({
  title,
  category,
  subType,
  style,
  details,
  promptOnly
}) => {
  try {
    const response = await post(`${apiEndpoints.eventCreativeImage}/alfred`, {
      title,
      category,
      subType,
      style,
      details,
      promptOnly
    })
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in generating event creative image')
    throw error
  }
}

// Face-centre a facilitator photo before it goes into the creative's circular
// frame. Best-effort: the server hands back the original if it can't crop, so a
// failure never blocks the upload.
const faceCropEventCreativePhoto = async (image) => {
  try {
    const response = await post(
      `${apiEndpoints.eventCreativeFaceCrop}/alfred`,
      { image }
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in face-cropping the facilitator photo')
    throw error
  }
}

export {
  faceCropEventCreativePhoto,
  fetchNewsletterDrafts,
  fetchNewsletterDraft,
  saveNewsletterDraft,
  updateNewsletterDraft,
  deleteNewsletterDraft,
  freezeNewsletterDraft,
  generateEventCreativeImage
}
